import React, { useMemo } from "react";
import "../App.css";
import SiteNav from "../components/SiteNav";
import DealerResourceSummary from "../components/DealerResourceSummary";
import usePageMeta from "../hooks/usePageMeta";
import { DEALERSHIP } from "../data/dealership";

function Guide() {
  const title = `${DEALERSHIP.name} (${DEALERSHIP.location}) Complaints, Reviews, and Buyer Guide`;
  const description =
    `Step-by-step buyer guide for ${DEALERSHIP.name} in ${DEALERSHIP.location}, covering complaints, reviews, used vehicles, trade-in checks, service records, finance paperwork, and Ohio reporting resources.`;

  usePageMeta({
    title,
    description,
  });

  const faqSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `How do I file a complaint about Findlay Chrysler in ${DEALERSHIP.location}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text:
              `Submit a complaint to the Ohio Attorney General and include documentation, photos, and dealer permit ${DEALERSHIP.dealerPermit}. You can also notify the Ohio DMV Commercial Licensing division.`,
          },
        },
        {
          "@type": "Question",
          name: `What red flags should I watch for at Findlay Chrysler in ${DEALERSHIP.location}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Look for incomplete inspection paperwork, missing certification statements, unexplained price changes, pressure to sign quickly, and undisclosed auction history.",
          },
        },
        {
          "@type": "Question",
          name: `Where can I read reviews about Findlay Chrysler in ${DEALERSHIP.location}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Check multiple platforms (Google, BBB customer reviews, and Yelp) and compare recent comments for patterns such as undisclosed defects, inspection concerns, and after-sale support.",
          },
        },
      ],
    }),
    []
  );

  return (
    <>
      <SiteNav />
      <div className="container">
        <div className="text-center mt-5 text-uppercase">
          <h1 className="h3">Findlay Chrysler Complaints, Reviews, and Buyer Guide</h1>
          <p className="lead">
            Independent consumer resource to help shoppers discover issues and
            file complaints if needed.
          </p>
        </div>
        <DealerResourceSummary />
        <div className="mt-4">
          <h4>Quick Links</h4>
          <ul>
            <li>
              <a
                href="https://www.google.com/search?q=findlay+chrysler+reviews"
                target="_blank"
                rel="noreferrer"
              >
                Findlay Chrysler reviews (Google)
              </a>
            </li>
            <li>
              <a
                href="https://www.ohioattorneygeneral.gov/individuals-and-families/consumers/file-a-complaint.aspx"
                target="_blank"
                rel="noreferrer"
              >
                File an Ohio Attorney General complaint ({DEALERSHIP.dealerPermit})
              </a>
            </li>
            <li>
              <a
                href="https://www.ohioattorneygeneral.gov/"
                target="_blank"
                rel="noreferrer"
              >
                Ohio Attorney General main site
              </a>
            </li>
          </ul>
        </div>
        <div className="mt-4">
          <h4>What to Check Before You Sign</h4>
          <ol>
            <li>
              Ask for a written inspection checklist and verify it is signed and
              dated. Ensure it covers brakes, steering electronics, and safety
              systems.
            </li>
            <li>
              Request the vehicle history report and confirm whether the vehicle
              was purchased at auction. Ask for proof of reconditioning.
            </li>
            <li>
              Verify the out-the-door price on paper before signing. Decline any
              last-minute changes or unexplained trade-in reductions.
            </li>
            <li>
              Test all driver assistance features (ACC, lane keeping, parking
              sensors) and refuse to sign if any are inoperable.
            </li>
            <li>
              Take photos of the vehicle and the signed documents before leaving
              the lot.
            </li>
          </ol>
        </div>
        <div className="mt-4">
          <h4>Red Flags Reported in Reviews</h4>
          <p>
            Shoppers searching for "Findlay Chrysler reviews" have noted:
          </p>
          <ul>
            <li>Undisclosed auction history and missing inspection details.</li>
            <li>Pressure to sign quickly with limited time to review contracts.</li>
            <li>Reduced trade-in values compared to prior verbal agreements.</li>
            <li>Safety features (adaptive cruise, parking brake) not functioning after purchase.</li>
            <li>Heavy reliance on AS-IS language instead of transparent disclosure.</li>
          </ul>
        </div>
        <div className="mt-4">
          <h4>If You Already Bought a Vehicle</h4>
          <ol>
            <li>
              Document every issue with photos, diagnostics, and service invoices.
            </li>
            <li>
              Email the dealer and request written responses. Keep all replies.
            </li>
            <li>
              File an Ohio Attorney General complaint and include dealer permit
              {DEALERSHIP.dealerPermit} plus your documentation.
            </li>
            <li>
              Notify the Ohio DMV Commercial Licensing division about unresolved
              safety defects.
            </li>
            <li>
              Consider an independent inspection to establish condition at time
              of sale.
            </li>
          </ol>
        </div>
        <div className="mt-4">
          <h4>Share Your Experience</h4>
          <p>
            Sharing accurate, time-stamped information helps other shoppers who
            search for "Findlay Chrysler complaints" or "Findlay Chrysler
            reviews." Provide dates, names, documents, and photos so regulators
            and other consumers can verify patterns.
          </p>
          <div className="container text-center d-grid mx-auto my-4">
            <a
              href="https://www.ohioattorneygeneral.gov/individuals-and-families/consumers/file-a-complaint.aspx"
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary active"
              type="button"
              id="cc"
              style={{ backgroundColor: "#0377fc !important" }}
            >
              FILE A COMPLAINT
            </a>
          </div>
        </div>
        <div className="mt-5">
          <h4>Additional Review Links</h4>
          <ul>
            <li>
              <a href={DEALERSHIP.bbbReviews} target="_blank" rel="noreferrer">
                BBB customer reviews: {DEALERSHIP.bbbReviews}
              </a>
            </li>
            <li>
              <a href={DEALERSHIP.yelp} target="_blank" rel="noreferrer">
                Yelp listing: {DEALERSHIP.yelp}
              </a>
            </li>
            <li>
              <a href={DEALERSHIP.driveFindlay} target="_blank" rel="noreferrer">
                Drive Findlay: {DEALERSHIP.driveFindlay}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

export default Guide;
